<template>
  <md-card class="component-available-time-setting">
    <md-card-header class="md-card-header-text md-card-header-primary">
      <div class="card-text" style="display: block;background: grey;">
        <h4 class="title">{{ $t("Flight Review Available Time") }}</h4>
      </div>
    </md-card-header>
    <md-card-content>
      <div
        v-for="(item, idx) in availableTimeArr"
        :key="'timeperiod_' + idx"
        class="period-block"
      >
        <div>
          <span class="title">{{ $t("Recurring Time") }}</span>
          <span v-if="item.repeatWeekly.length > 0" class="ml-10"
            >{{ $t("From") }}
            {{
              item.startDate ? moment(item.startDate).format("YYYY-MM-DD") : "-"
            }}
            to
            {{
              item.endDate ? moment(item.endDate).format("YYYY-MM-DD") : "-"
            }},
            {{ $t("Every") }}
            {{ item.repeatWeekly.join(", ") }}, {{ $t("each day from") }}
            {{ item.startTime || "-" }} to {{ item.endTime || "-" }}</span
          >
        </div>
        <div class="mt-10">
          <span class="title">{{ $t("Date") }}</span>
          <span class="date-container">
            <md-datepicker
              v-model="item.startDate"
              :md-model-type="Number"
              md-immediately
              :md-open-on-focus="item.status !== 'detail'"
              :md-disabled-dates="disabledDates"
            />
            <span class="to">to</span>
            <md-datepicker
              v-model="item.endDate"
              :md-model-type="Number"
              md-immediately
              :md-open-on-focus="item.status !== 'detail'"
              :md-disabled-dates="disabledDates"
            />
          </span>
        </div>
        <div class="mt-10">
          <span class="title">{{ $t("Time") }}</span>
          <span class="ml-10">
            <vue-timepicker
              v-model="item.startTime"
              :hour-range="[[6, 21]]"
              hide-disabled-hours
              :disabled="item.status === 'detail'"
              close-on-complete
            >
            </vue-timepicker>
            &nbsp;&nbsp; to &nbsp;&nbsp;
            <vue-timepicker
              v-model="item.endTime"
              :hour-range="[[6, 21]]"
              hide-disabled-hours
              :disabled="item.status === 'detail'"
              close-on-complete
            >
            </vue-timepicker>
          </span>
        </div>
        <div class="duration-container">
          <span class="title">{{ $t("Duration") }}</span>
          <md-field class="right">
            <md-select
              v-model="item.duration"
              :disabled="item.status === 'detail'"
            >
              <md-option :value="60">60 minutes</md-option>
              <md-option :value="90">90 minutes</md-option>
              <md-option :value="120">120 minutes</md-option>
            </md-select>
          </md-field>
        </div>

        <div>
          <span class="title">{{ $t("Occurs on") }}</span>
          <span class="weeks-container">
            <md-checkbox
              v-for="subItem in dayOfweek"
              :key="subItem"
              :disabled="item.status === 'detail'"
              v-model="item.repeatWeekly"
              :value="subItem"
              >{{ $t(subItem) }}</md-checkbox
            >
          </span>
        </div>
        <div class="tac">
          <md-button
            class="md-raised mt-4 mr-10"
            :disabled="btnLoading"
            @click="removePeriod(idx, item.id)"
          >
            <md-icon class="md-size-1x">delete_outline</md-icon>
            {{ $t("Remove") }}
          </md-button>
          <md-button
            v-if="item.status === 'edit'"
            class="md-raised mt-4 mr-10"
            @click="item.status = 'detail'"
          >
            {{ $t("Cancel") }}
          </md-button>

          <md-button
            :disabled="btnLoading"
            class="md-raised md-primary mt-4"
            @click="updateAvailableTime(item)"
          >
            <span v-if="item.status === 'detail'"
              ><md-icon class="md-size-1x"> edit </md-icon
              >{{ $t("Edit") }}</span
            >
            <span v-else
              ><md-icon class="md-size-1x">save</md-icon>{{ $t("Save") }}</span
            >
          </md-button>
        </div>
      </div>
      <div v-if="availableTimeArr.length < 5" class="tac">
        <md-button class="md-raised md-primary mt-4" @click="addNewPeriod"
          ><md-icon class="md-size-2x">add</md-icon>
          {{ $t("Add Additional Available Time Slots") }}
        </md-button>
      </div>
    </md-card-content>
  </md-card>
</template>

<script>
import VueTimepicker from "vue2-timepicker";
import "vue2-timepicker/dist/VueTimepicker.css";
import {
  getReviewerAvailableSchedule,
  updateAvailableSchedule,
  addAvailableSchedule,
  delAvailableSchedule,
  getAvaliableEventsByScheduleId
} from "@/services/flightReview.service.js";
import {
  timeZeroPadding,
  hoursAndMinutesToSeconds,
  secondsToHoursAndMinutes
} from "@/helpers/utils";
import moment from "moment";
import swal from "sweetalert2";

export default {
  provide() {
    return {
      $validator: this.$validator
    };
  },
  $_veeValidate: {
    validator: "new"
  },
  components: {
    VueTimepicker
  },
  data() {
    return {
      dayOfweek: [
        this.$t("Monday"),
        this.$t("Tuesday"),
        this.$t("Wednesday"),
        this.$t("Thursday"),
        this.$t("Friday"),
        this.$t("Saturday"),
        this.$t("Sunday")
      ],
      btnLoading: false,
      locale: window.localStorage.getItem("locale"),
      availableTimeArr: [this.initTimeSlotOption()],
      disabledDates: date => {
        const day = date.getDay()
        return new Date() > +date
      }
    };
  },
  async mounted() {
    this.loadAvailableSchedule();
  },
  methods: {
    async loadAvailableSchedule() {
      const res = await getReviewerAvailableSchedule();
      if (!res) return;
      const schedule = res.schedule || [];
      if (schedule.length === 0) return;
      let availableTimeArr = [];
      for (let item of schedule) {
        let curTime = {};
        let availableSchedule = item.available_schedule;
        curTime.startDate = item.start_time.$date;
        curTime.endDate = item.end_time.$date;
        curTime.id = item._id.$oid;
        curTime.duration = item.duration || 60;
        if (availableSchedule) {
          curTime.status = "detail";
          let weeklyIdx = [];
          let startTime, endTime;
          delete availableSchedule.repeat;
          for (let key in availableSchedule) {
            let _availableSchedule = availableSchedule[key];
            if (_availableSchedule.available) {
              if (!startTime) {
                startTime = secondsToHoursAndMinutes(
                  _availableSchedule.start_time
                );
                endTime = secondsToHoursAndMinutes(_availableSchedule.end_time);
                curTime.startTime = `${timeZeroPadding(
                  startTime.hours
                )}:${timeZeroPadding(startTime.minutes)}`;
                curTime.endTime = `${timeZeroPadding(
                  endTime.hours
                )}:${timeZeroPadding(endTime.minutes)}`;
              }

              weeklyIdx.push(key);
            }
          }
          curTime.repeatWeekly = weeklyIdx.map(key => this.dayOfweek[+key - 1]);
        }
        availableTimeArr.push(curTime);
      }
      this.availableTimeArr = availableTimeArr;
    },
    updateAvailableTime(item) {
      let time = {};
      const overlapsTip =
        window.localStorage.getItem("locale") === "fr"
          ? "Votre calendrier chevauche. Veuillez vérifier et ajuster en conséquence."
          : "Your calendar schedule overlaps. Please review and adjust accordingly.";
      switch (item.status) {
        case "add":
        case "edit":
          if (!item.startDate || !item.endDate) {
            this.$notify({
              message: this.$t("Please input startDate and endDate"),
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning",
              timeout: 3000
            });
            return;
          }
          if (
            item.startDate <
            moment()
              .startOf("day")
              .valueOf()
          ) {
            this.$notify({
              message: this.$t("Start time cannot be earlier than today"),
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning",
              timeout: 3000
            });
            return;
          }

          if (item.startDate > item.endDate) {
            this.$notify({
              message: this.$t("start date must less than end date"),
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning",
              timeout: 3000
            });
            return;
          }
          if (!item.startTime || !item.endTime) {
            this.$notify({
              message: this.$t("Please input startTime and endTime"),
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning",
              timeout: 3000
            });
            return;
          }

          if (item.startTime > item.endTime) {
            this.$notify({
              message: this.$t("start time must less than end time"),
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning",
              timeout: 3000
            });
            return;
          }

          if (!item.duration) {
            this.$notify({
              message: this.$t("Please input exam duration"),
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning",
              timeout: 3000
            });
            return;
          }

          this.btnLoading = true;
          time.start_time = item.startDate / 1000;
          time.end_time = item.endDate / 1000 + 24 * 60 * 60 - 1;
          this.dayOfweek.forEach((key, idx) => {
            if (item.repeatWeekly.indexOf(key) > -1) {
              time[idx + 1] = {
                available: true,
                start_time: hoursAndMinutesToSeconds(item.startTime),
                end_time: hoursAndMinutesToSeconds(item.endTime)
              };
            } else {
              time[idx + 1] = {
                available: false
              };
            }
          });
          time.duration = item.duration;

          if (item.status === "add") {
            addAvailableSchedule(time)
              .then(res => {
                this.$notify({
                  message: this.$t("add flight review package successful"),
                  horizontalAlign: "center",
                  verticalAlign: "top",
                  type: "success",
                  timeout: 3000
                });
                item.status = "detail";
                item.id = res.$oid;
              })
              .catch(err => {
                let message = err.data.message;
                if (err.data.code === 1011) {
                  message = overlapsTip;
                }
                this.$notify({
                  message,
                  horizontalAlign: "center",
                  verticalAlign: "top",
                  type: "warning",
                  timeout: 3000
                });
              })
              .finally(() => {
                this.btnLoading = false;
              });
          } else {
            time.id = item.id;
            updateAvailableSchedule(time)
              .then(() => {
                this.$notify({
                  message: this.$t("Update flight review package successful"),
                  horizontalAlign: "center",
                  verticalAlign: "top",
                  type: "success",
                  timeout: 3000
                });
                item.status = "detail";
              })
              .catch(err => {
                let message = err.data.message;
                if (err.data.code === 1011) {
                  message = overlapsTip;
                }
                this.$notify({
                  message,
                  horizontalAlign: "center",
                  verticalAlign: "top",
                  type: "warning",
                  timeout: 3000
                });
              })
              .finally(() => {
                this.btnLoading = false;
              });
          }

          break;

        case "detail":
          item.status = "edit";
          break;
      }
    },
    moment(date) {
      return moment(date);
    },
    initTimeSlotOption() {
      return {
        status: "add", // types: add | detail | edit
        repeatWeekly: [],
        startTime: "07:00",
        endTime: "21:00",
        startDate: undefined,
        endDate: undefined,
        id: undefined,
        duration: 60
      };
    },
    addNewPeriod() {
      this.availableTimeArr.push(this.initTimeSlotOption());
    },
    delAvailableScheduleFn(idx, id) {
      delAvailableSchedule(id)
        .then(() => {
          this.availableTimeArr.splice(idx, 1);
        })
        .finally(() => {
          this.btnLoading = false;
        });
    },
    async removePeriod(idx, id) {
      const _self = this;
      if (!id) {
        swal
          .fire({
            text: this.$t("Do you want to remove this time slot?"),
            showCancelButton: true,
            confirmButtonClass: "md-button md-success",
            cancelButtonClass: "md-button md-danger",
            confirmButtonText: this.$t("Yes"),
            cancelButtonText: this.$t("Cancel"),
            buttonsStyling: false
          })
          .then(async function({ value }) {
            if (!value) return;
            _self.availableTimeArr.splice(idx, 1);
          });
        return;
      }
      _self.btnLoading = true;
      try {
        const events = await getAvaliableEventsByScheduleId(id);
        swal
          .fire({
            text:
              events.length > 0
                ? _self.$t(
                    "You have a flight review in this schedule. Are you sure you want to delete it?"
                  )
                : this.$t("Do you want to remove this time slot?"),
            showCancelButton: true,
            confirmButtonClass: "md-button md-success",
            cancelButtonClass: "md-button md-danger",
            confirmButtonText: _self.$t("Yes"),
            cancelButtonText: _self.$t("Cancel"),
            buttonsStyling: false
          })
          .then(({ value }) => {
            if (!value) {
              _self.btnLoading = false;
              return;
            }
            _self.delAvailableScheduleFn(idx, id);
          });
      } catch (err) {
        _self.btnLoading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.component-available-time-setting {
  .tac {
    text-align: center;
  }
  .tar {
    text-align: right;
  }
  .date-container {
    display: inline-flex;
    align-items: center;
    margin-left: 10px;
    .md-date-icon {
      display: none;
    }
    .md-datepicker:after {
      width: auto;
    }
    .md-field:before {
      background-color: transparent !important;
    }
  }
  .to {
    display: inline-block;
    margin: 0 20px;
  }
  .duration-container {
    display: flex;
    align-items: center;
    .right {
      margin-left: 6px;
      padding-left: 12px;
      width: 180px;
    }
  }
  .weeks-container {
    margin-left: 10px;
    position: relative;
    top: 6px;
  }
  .period-block {
    border: 1px dashed #ccc;
    padding: 12px;
    margin-bottom: 24px;
  }
}
</style>
